/**
 * Shopify Virtual Size Inventory (BRD v1.0)
 * PDP Client Controller
 *
 * Handles:
 * 1. Listening to size option changes on the PDP
 * 2. When an out-of-stock size (e.g. XS) is selected:
 *    - Uses the physical source variant (e.g. S) for the cart add API call so Shopify
 *      accepts the order without throwing "variant is already sold out"
 *    - Injects internal fulfillment metadata (_requested_size: XS, _source_size: S, etc.)
 *    - Ensures the Add to Cart button is enabled
 *    - Hides any previous "sold out" error messages
 * 3. In cart drawer / cart page / checkout:
 *    - The customer sees requested size (XS) everywhere
 *    - The customer never sees that physical inventory came from S
 * 4. Toggles the non-returnable policy badge on the PDP
 *
 * Fix v1.1:
 * - Corrected cart item ID to use sourceVariantId (not ownVariantId) in
 *   handleSizeChange(), fetch FormData, and fetch JSON body so Shopify never
 *   receives the out-of-stock XS variant and rejects with "already sold out".
 */

(function () {
  'use strict';

  function getDetailsWrapper() {
    return document.querySelector('[js-product-details]') || document.querySelector('.product_details');
  }

  function isVirtualSizeDisabledForPage() {
    const vsiScript = document.querySelector('[data-virtual-size-inventory]');
    if (vsiScript) {
      try {
        const vsiData = JSON.parse(vsiScript.textContent);
        if (vsiData && vsiData.enabled === false) return true;
      } catch (e) {}
    } else {
      const details = getDetailsWrapper();
      if (details) return true;
    }

    const catInput = document.querySelector('input[name*="[_Product category]"], input[name*="Product category"]');
    const catVal = (catInput && catInput.value ? catInput.value : '').toLowerCase();
    if (catVal.includes('saree')) return true;

    const path = window.location.pathname.toLowerCase();
    if (path.includes('-saree') || path.includes('/saree') || path.includes('sarees')) return true;

    return false;
  }

  function findVirtualRadioByVariantId(variantId) {
    if (variantId == null || variantId === '') return null;
    return document.querySelector(
      `input[type="radio"][data-virtual-size="true"][varid="${variantId}"]`
    );
  }

  function virtualSizePayloadFromRadio(radio) {
    const requestedSize = radio.getAttribute('data-requested-size') || radio.value || '';
    const sourceSize = radio.getAttribute('data-source-size') || '';
    const ownVariantId = radio.getAttribute('varid') || '';
    const sourceVariantId = radio.getAttribute('data-source-variant-id') || ownVariantId;
    return {
      sourceVariantId,
      properties: {
        'Requested Size': requestedSize,
        '_requested_size': requestedSize,
        '_requested_variant_id': ownVariantId,
        '_requested_price': radio.getAttribute('data-product-price') || '',
        '_source_size': sourceSize,
        '_source_variant_id': sourceVariantId,
        '_source_sku': radio.getAttribute('data-source-sku') || '',
        '_alteration_required': 'true',
        '_alteration_instruction':
          radio.getAttribute('data-alteration-instruction') || `Alter ${sourceSize} -> ${requestedSize}`,
        '_non_returnable_due_to_alteration': 'true',
        '_Delivery type': 'RTS'
      }
    };
  }

  function rewriteJsonItemsForVirtualSizes(parsedBody) {
    if (!parsedBody || !Array.isArray(parsedBody.items)) return false;
    let changed = false;
    parsedBody.items.forEach((item) => {
      const radio = findVirtualRadioByVariantId(item && item.id);
      if (!radio) return;
      const mapped = virtualSizePayloadFromRadio(radio);
      item.id = Number(mapped.sourceVariantId) || mapped.sourceVariantId;
      item.properties = Object.assign({}, item.properties || {}, mapped.properties);
      changed = true;
    });
    return changed;
  }

  function rewriteFormDataItemsForVirtualSizes(body) {
    if (!(body instanceof FormData)) return false;
    let changed = false;
    const keys = [];
    if (body.has('id')) keys.push('id');
    for (let i = 0; i < 8; i++) {
      if (body.has(`items[${i}][id]`)) keys.push(`items[${i}][id]`);
    }
    keys.forEach((key) => {
      const radio = findVirtualRadioByVariantId(body.get(key));
      if (!radio) return;
      const mapped = virtualSizePayloadFromRadio(radio);
      body.set(key, mapped.sourceVariantId);
      const match = key.match(/^items\[(\d+)\]\[id\]$/);
      const prefix = match ? `items[${match[1]}][properties]` : 'properties';
      Object.keys(mapped.properties).forEach((propKey) => {
        body.set(`${prefix}[${propKey}]`, mapped.properties[propKey]);
      });
      changed = true;
    });
    return changed;
  }

  function setTwiningHiddenVariantId(selectedRadio, variantId) {
    if (!selectedRadio || !document.querySelector('.twining-tab-warp')) return false;
    const isMale = (selectedRadio.name || '').indexOf('Product2') !== -1;
    const input = document.getElementById(isMale ? 'male_variant_input' : 'female_variant_input');
    if (input && variantId) {
      input.value = variantId;
      input.removeAttribute('disabled');
    }
    return true;
  }

  // Upsell adds (Pre Drape, Fall & Pico) use items[0] for the add-on, not the saree.
  function isUpsellCartAdd(body, ownVariantId, sourceVariantId) {
    if (!body) return false;
    const ownId = ownVariantId ? String(ownVariantId) : '';
    const sourceId = sourceVariantId ? String(sourceVariantId) : '';
    const isSareeVariant = (id) => {
      const value = id != null ? String(id) : '';
      if (!value) return false;
      return value === ownId || value === sourceId;
    };

    if (body instanceof FormData) {
      if (body.has('items[1][id]')) return true;
      if (body.get('items[0][properties][_Twining product]') === 'Yes') return true;
      if (body.get('items[0][properties][_type]') === 'sub product') return true;
      const lineId = body.get('items[0][id]');
      return Boolean(lineId) && !isSareeVariant(lineId);
    }

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        const items = parsed && Array.isArray(parsed.items) ? parsed.items : [];
        if (items.length > 1) return true;
        const first = items[0];
        if (!first) return false;
        if (first.properties && (first.properties._type === 'sub product' || first.properties['_Twining product'] === 'Yes')) return true;
        return Boolean(first.id) && !isSareeVariant(first.id);
      } catch (e) {
        return false;
      }
    }

    return false;
  }

  function getActiveSizeRadio(context) {
    const root = context || document;
    const checked = Array.from(root.querySelectorAll(
      'fieldset[js-size-options] input[type="radio"]:checked, .o-Size input[type="radio"]:checked, input[type="radio"][name^="Size"]:checked, input[type="radio"][name^="Confi size"]:checked, input[type="radio"][name^="Confi Size"]:checked'
    ));
    if (checked.length === 0) return null;
    const visible = checked.find((r) => r.offsetParent !== null || r.closest('.product-form, product-form, [js-product-details]'));
    return visible || checked[checked.length - 1];
  }

  function setProperty(propKey, value) {
    const inputs = document.querySelectorAll(`[js-virtual-prop="${propKey}"]`);
    inputs.forEach((input) => {
      input.value = value;
      input.removeAttribute('disabled');
    });
  }

  function clearProperties() {
    const inputs = document.querySelectorAll('[js-virtual-prop]');
    inputs.forEach((input) => {
      input.value = '';
      input.setAttribute('disabled', 'disabled');
    });
    const forms = document.querySelectorAll('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
    forms.forEach((form) => {
      form.querySelectorAll(
        'input[name="items[0][properties][Requested Size]"], input[name="items[0][properties][_requested_size]"], input[name="items[0][properties][_requested_variant_id]"], input[name="items[0][properties][_requested_price]"], input[name="items[0][properties][_source_size]"], input[name="items[0][properties][_source_variant_id]"], input[name="items[0][properties][_source_sku]"], input[name="items[0][properties][_alteration_required]"], input[name="items[0][properties][_alteration_instruction]"], input[name="items[0][properties][_non_returnable_due_to_alteration]"]'
      ).forEach((el) => {
        el.value = '';
        el.setAttribute('disabled', 'disabled');
      });
    });
    try {
      sessionStorage.removeItem('kalki_vsi_cart_items');
    } catch (e) {}
  }

  function clearFormErrors() {
    const errorWrappers = document.querySelectorAll('.product-form__error-message-wrapper');
    errorWrappers.forEach((el) => {
      el.setAttribute('hidden', '');
      const msg = el.querySelector('.product-form__error-message');
      if (msg) msg.textContent = '';
    });
  }

  function enableAddToCartButtons() {
    const addButtons = document.querySelectorAll(
      'button[name="add"], .product-form__submit, [id^="ProductSubmitButton"]'
    );
    addButtons.forEach((btn) => {
      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('disabled', 'unavailable-variant');
      const span = btn.querySelector('span:first-child, span');
      if (span) {
        const txt = span.textContent.trim().toLowerCase();
        if (txt.includes('sold out') || txt.includes('unavailable')) {
          span.textContent =
            window.variantStrings && window.variantStrings.addToCart
              ? window.variantStrings.addToCart
              : 'Add to Cart';
        }
      }
    });

    const stickyBtns = document.querySelectorAll('.sticky-cart-btn, [js-sticky-atc-btn], .sticky-add-to-cart-button');
    stickyBtns.forEach((sBtn) => {
      sBtn.removeAttribute('disabled');
      sBtn.removeAttribute('aria-disabled');
      sBtn.classList.remove('disabled');
      const span = sBtn.querySelector('span');
      if (span && span.textContent.toLowerCase().includes('sold out')) {
        span.textContent =
          window.variantStrings && window.variantStrings.addToCart
            ? window.variantStrings.addToCart
            : 'Add to Cart';
      }
    });
  }

  function patchVariantSelects() {
    const customVariantSelects = window.customElements && window.customElements.get('variant-selects');
    const targetProto = customVariantSelects ? customVariantSelects.prototype : (typeof VariantSelects !== 'undefined' ? VariantSelects.prototype : null);
    if (targetProto && !targetProto._vsiPatched) {
      const origToggleAddButton = targetProto.toggleAddButton;
      if (typeof origToggleAddButton === 'function') {
        targetProto.toggleAddButton = function (disable, text, modifyClass) {
          const activeRadio = getActiveSizeRadio();
          const isVirtual = activeRadio && activeRadio.getAttribute('data-virtual-size') === 'true';
          if (isVirtual) {
            return origToggleAddButton.call(this, false, (window.variantStrings && window.variantStrings.addToCart) || 'Add to Cart', false);
          }
          return origToggleAddButton.call(this, disable, text, modifyClass);
        };
        targetProto._vsiPatched = true;
      }
    }
  }

  function observeAddToCartButtons() {
    const addButtons = document.querySelectorAll('button[name="add"], [id^="ProductSubmitButton"]');
    addButtons.forEach((btn) => {
      if (btn._vsiObserved) return;
      btn._vsiObserved = true;
      const observer = new MutationObserver(() => {
        const activeRadio = getActiveSizeRadio();
        if (activeRadio && activeRadio.getAttribute('data-virtual-size') === 'true') {
          if (btn.hasAttribute('disabled') || btn.classList.contains('disabled')) {
            enableAddToCartButtons();
          }
        }
      });
      observer.observe(btn, { attributes: true, attributeFilter: ['disabled', 'class', 'aria-disabled'] });
    });
  }

  function updateVirtualDeliveryMessage() {
    const activeRadio = getActiveSizeRadio();
    if (!activeRadio || activeRadio.getAttribute('data-virtual-size') !== 'true') return;

    // 1. Get Priority express date from PDP delivery wrapper
    const expressEl = document.querySelector('[js-product-express-delivery-time]');
    const defaultEl = document.querySelector('[js-product-default-delivery-time]');
    const expressDate = (expressEl && expressEl.textContent.trim()) || (defaultEl && defaultEl.textContent.trim());

    // 2. Ensure form properties reflect Priority express shipping
    const forms = document.querySelectorAll('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
    forms.forEach((form) => {
      // Remove MTO Estimated delivery
      form.querySelectorAll('[js-estimated-delivery-input], input[name="items[0][properties][Estimated delivery]"]').forEach((el) => el.remove());

      if (expressDate) {
        let expressInput = form.querySelector('[js-estimated-express-delivery-input], input[name="items[0][properties][Priority express shipping]"]');
        if (expressInput) {
          expressInput.value = expressDate;
          expressInput.removeAttribute('disabled');
        } else {
          form.insertAdjacentHTML(
            'afterbegin',
            `<input type="hidden" js-estimated-express-delivery-input name="items[0][properties][Priority express shipping]" value="${expressDate}" />`
          );
        }
      }

      // Ensure Express Delivery attribute
      let attrInput = form.querySelector('[express_delivery_cart_attributes]');
      if (!attrInput) {
        form.insertAdjacentHTML(
          'afterbegin',
          `<input type="hidden" express_delivery_cart_attributes name="attributes[Express Delivery]" value="Express Delivery" />`
        );
      }

      // Ensure _Delivery type = RTS
      form.querySelectorAll('input[name="items[0][properties][_Delivery type]"]').forEach((el) => {
        el.value = 'RTS';
      });
    });

    // 3. Update the message box above the height selector (.additional_delivery_massage_text)
    const mtoMsg = document.querySelector('.additional_delivery_massage_text');
    if (mtoMsg) {
      mtoMsg.style.display = '';
      mtoMsg.classList.remove('hidden');
      if (expressDate) {
        mtoMsg.innerHTML = `Priority express shipping : ${expressDate}`;
      } else if (mtoMsg.innerHTML.includes('This size is sold out')) {
        mtoMsg.innerHTML = mtoMsg.innerHTML.replace(
          /This size is sold out and will be made to order for you\.\s*(<br>\s*)?/i,
          ''
        );
      }
    }

    // 4. Ensure express delivery wrapper is visible and MTO is hidden
    document.querySelectorAll('.delivery-wrapper-with-express').forEach((el) => el.classList.remove('hidden'));
    document.querySelectorAll('[js-delivery-wrapper]').forEach((el) => el.classList.add('hidden'));
  }

  function updatePdpPrice(selectedRadio) {
    if (!selectedRadio) return;
    let priceFormatted = selectedRadio.getAttribute('data-product-price');
    let priceRaw = selectedRadio.getAttribute('data-product-main-price');
    let comparePriceFormatted = selectedRadio.getAttribute('data-product-compare-price');
    let discountText = selectedRadio.getAttribute('data-product-discount');
    const ownVariantId = selectedRadio.getAttribute('varid');
    const isVirtual = selectedRadio.getAttribute('data-virtual-size') === 'true';

    if (!priceFormatted && ownVariantId) {
      const vSelects = document.querySelector('variant-selects');
      const variantList = vSelects && typeof vSelects.getVariantData === 'function' ? vSelects.getVariantData() : [];
      const v = variantList.find((item) => String(item.id) === String(ownVariantId));
      if (v) {
        const symbol = document.querySelector('[data-shop-currency]').dataset.shopCurrency || '$';
        priceFormatted = symbol + (v.price / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 }) + ' ' + Shopify.currency.active;
        priceRaw = (v.price / 100).toFixed(2);
        if (v.compare_at_price && v.compare_at_price > v.price) {
          comparePriceFormatted = symbol + (v.compare_at_price / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 }) + ' ' + Shopify.currency.active;
          const pct = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
          discountText = pct + '% OFF';
        }
      }
    }

    if (!priceFormatted && !priceRaw) return;

    // 1. Regular price element
    const reg = document.querySelector('.price__regular .price-item--regular');
    if (reg && priceFormatted) {
      reg.innerHTML = priceFormatted;
      if (priceRaw) reg.setAttribute('data-pdp-product-price', priceRaw);
    }

    // 2. Sale price element
    const sale = document.querySelector('.price__sale .price-item--sale');
    if (sale && priceFormatted) {
      sale.textContent = priceFormatted;
      if (priceRaw) sale.setAttribute('data-pdp-product-price', priceRaw);
    }

    // 3. Compare at price element
    const comp = document.querySelector('.price__sale s.price-item--regular');
    if (comp) {
      if (comparePriceFormatted) {
        comp.innerHTML = comparePriceFormatted;
        const parentSpan = comp.closest('span');
        if (parentSpan) parentSpan.classList.remove('hidden');
      } else {
        const parentSpan = comp.closest('span');
        if (parentSpan) parentSpan.classList.add('hidden');
      }
    }

    // 4. Price container classes
    const priceContainer = document.querySelector('.Product_price_main_new, .price');
    if (priceContainer) {
      if (isVirtual) {
        priceContainer.classList.remove('price--sold-out');
      }
      if (comparePriceFormatted) {
        priceContainer.classList.add('price--on-sale');
      } else {
        priceContainer.classList.remove('price--on-sale');
      }
    }

    // 5. Discount badge
    if (discountText) {
      const discountLabel = document.querySelector('.Product_price_save_label');
      if (discountLabel) discountLabel.textContent = discountText;
      const savingPdp = document.querySelector('.data-saving-pdp');
      if (savingPdp) savingPdp.setAttribute('data-saving', discountText);
      const saveBox = document.querySelector('.Product_price_save_new');
      if (saveBox) saveBox.style.display = '';
    } else {
      const saveBox = document.querySelector('.Product_price_save_new');
      if (saveBox) saveBox.style.display = 'none';
    }

    // 6. ATC and sticky button prices
    document.querySelectorAll('[js-pdp-original-price], #aditional_costtotal').forEach((el) => {
      if (priceFormatted) {
        el.textContent = priceFormatted;
        el.setAttribute('data-price', priceFormatted);
      }
    });

    // 7. Sticky compare price
    document.querySelectorAll('[js-pdp-sticky-compare-at-price]').forEach((el) => {
      el.textContent = comparePriceFormatted || '';
    });

    // 8. Trigger sumTotalPrice event so any addons / upsells calculate correctly
    document.dispatchEvent(new CustomEvent('sumTotalPrice:run'));
  }

  function handleSizeChange(selectedRadio) {
    if (!selectedRadio) return;

    // Immediately display the selected variant's own price
    updatePdpPrice(selectedRadio);
    setTimeout(() => updatePdpPrice(selectedRadio), 100);
    setTimeout(() => updatePdpPrice(selectedRadio), 350);
    setTimeout(() => updatePdpPrice(selectedRadio), 700);

    const isVirtual = selectedRadio.getAttribute('data-virtual-size') === 'true';
    const detailsWrapper = getDetailsWrapper();
    const ownVariantId = selectedRadio.getAttribute('varid');

    if (isVirtual) {
      const requestedSize = selectedRadio.getAttribute('data-requested-size') || selectedRadio.value;
      const sourceSize = selectedRadio.getAttribute('data-source-size') || '';
      const sourceVariantId = selectedRadio.getAttribute('data-source-variant-id') || ownVariantId;
      const sourceSku = selectedRadio.getAttribute('data-source-sku') || '';
      const alterationInstruction =
        selectedRadio.getAttribute('data-alteration-instruction') || `Alter ${sourceSize} -> ${requestedSize}`;

      // FIX v1.1: Submit the PHYSICAL SOURCE variant (e.g. S) which has actual inventory in Shopify.
      // Use sourceVariantId — NOT ownVariantId — so Shopify never sees the out-of-stock XS variant
      // and does not throw "variant is already sold out". This prevents the need to enable
      // "Continue selling when out of stock" in Shopify Admin.
      if (!setTwiningHiddenVariantId(selectedRadio, sourceVariantId)) {
        const variantInputs = document.querySelectorAll(
          'input[name="items[0][id]"], input[name="id"].product-variant-id, select[name="id"].product-variant-id, input.product-variant-id, input[name="id"]'
        );
        variantInputs.forEach((inp) => {
          if (sourceVariantId) {
            inp.value = sourceVariantId; // ✅ FIXED: was ownVariantId
            inp.removeAttribute('disabled');
          }
        });
      }

      // 2. Clear previous sold-out error banner
      clearFormErrors();

      // 3. Keep the Add to Cart button enabled and clickable
      enableAddToCartButtons();
      setTimeout(enableAddToCartButtons, 100);
      setTimeout(enableAddToCartButtons, 300);
      setTimeout(enableAddToCartButtons, 600);

      const pFormatted = selectedRadio.getAttribute('data-product-price') || '';
      const pRaw = selectedRadio.getAttribute('data-product-main-price') || '';

      // 4. Populate fulfillment metadata (both customer-visible for checkout & hidden for Vinculum)
      setProperty('checkout_size', requestedSize);
      setProperty('requested_size', requestedSize);
      setProperty('requested_variant_id', ownVariantId);
      setProperty('requested_price', pFormatted);
      setProperty('source_size', sourceSize);
      setProperty('source_variant_id', sourceVariantId);
      setProperty('source_sku', sourceSku);
      setProperty('alteration_required', 'true');
      setProperty('alteration_instruction', alterationInstruction);
      setProperty('non_returnable', 'true');

      // Set delivery type to RTS as requested
      const delInputs = document.querySelectorAll('input[name="items[0][properties][_Delivery type]"]');
      delInputs.forEach((inp) => {
        inp.value = 'RTS';
      });

      try {
        const map = JSON.parse(sessionStorage.getItem('kalki_vsi_cart_items') || '{}');
        map[String(sourceVariantId)] = {
          requestedSize: requestedSize,
          priceFormatted: pFormatted,
          priceRaw: pRaw
        };
        sessionStorage.setItem('kalki_vsi_cart_items', JSON.stringify(map));
      } catch (e) {}

      // 5. Update return policy UI to Non-Returnable
      if (detailsWrapper) {
        detailsWrapper.classList.add('non-returnable', 'non-returnable-variant');
      }

      // 6. Keep additional delivery message visible (remove sold out / MTO prefix for virtual size)
      updateVirtualDeliveryMessage();
      setTimeout(updateVirtualDeliveryMessage, 50);
      setTimeout(updateVirtualDeliveryMessage, 150);
      setTimeout(updateVirtualDeliveryMessage, 300);
    } else {
      // Standard in-stock size selected
      const mtoMsg = document.querySelector('.additional_delivery_massage_text');
      if (mtoMsg) {
        mtoMsg.style.display = '';
      }

      if (!setTwiningHiddenVariantId(selectedRadio, ownVariantId)) {
        const variantInputs = document.querySelectorAll(
          'input[name="items[0][id]"], input[name="id"].product-variant-id, select[name="id"].product-variant-id, input.product-variant-id'
        );
        variantInputs.forEach((inp) => {
          if (ownVariantId) {
            inp.value = ownVariantId;
          }
        });
      }

      // Clear virtual fulfillment metadata
      clearProperties();

      // Revert non-returnable status if standard variant is returnable
      const isNonReturnableVariant = selectedRadio.getAttribute('returnable') === 'false';
      const isStitched = document.querySelector('.o-Stitched input:checked[value="Stitched"]');

      if (!isNonReturnableVariant && !isStitched && detailsWrapper) {
        detailsWrapper.classList.remove('non-returnable', 'non-returnable-variant');
      }
    }
  }

  function applyVirtualRtsSign() {
    if (isVirtualSizeDisabledForPage()) return;
    const vRadios = document.querySelectorAll(
      'input[type="radio"][data-virtual-size="true"]'
    );
    vRadios.forEach((radio) => {
      const label = document.querySelector(`label[for="${radio.id}"]`);
      if (label) {
        label.classList.add('stock-item-icon');
        label.setAttribute('data-variant-rts-sign', 'true');
        const sourceQty = radio.getAttribute('data-source-qty');
        if (sourceQty && parseInt(sourceQty) > 0 && parseInt(sourceQty) < 3) {
          label.classList.add('stock-item-qty');
          let span = label.querySelector('.stock-item-left-active');
          if (!span) {
            span = document.createElement('span');
            span.className = 'stock-item-left-active';
            label.appendChild(span);
          }
          if (!span.textContent.trim()) {
            span.textContent = `${sourceQty} left`;
          }
        }
      }
    });
  }

  function initVirtualSizeInventory() {
    if (isVirtualSizeDisabledForPage()) return;
    applyVirtualRtsSign();
    setTimeout(applyVirtualRtsSign, 150);
    setTimeout(applyVirtualRtsSign, 500);
    const sizeRadios = document.querySelectorAll(
      'fieldset[js-size-options] input[type="radio"], .o-Size input[type="radio"], input[type="radio"][name^="Size"], input[type="radio"][name^="Confi size"], input[type="radio"][name^="Confi Size"]'
    );

    if (!sizeRadios || sizeRadios.length === 0) return;

    // Attach change listener to all size radios
    sizeRadios.forEach((radio) => {
      radio.addEventListener('change', function () {
        handleSizeChange(this);
      });
    });

    // Support delegate clicks on size labels
    document.addEventListener('click', function (e) {
      const label = e.target.closest('label[input-value]');
      if (!label) return;
      const forId = label.getAttribute('for');
      if (forId) {
        const targetInput = document.getElementById(forId);
        if (
          targetInput &&
          (targetInput.name.startsWith('Size') ||
            targetInput.name.startsWith('Confi size') ||
            targetInput.name.startsWith('Confi Size'))
        ) {
          setTimeout(() => handleSizeChange(targetInput), 20);
        }
      }
    });

    // Hook into VariantSelects and observe Add to Cart buttons to prevent Sold Out overwrites
    patchVariantSelects();
    observeAddToCartButtons();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function () {
        const activeRadio = getActiveSizeRadio();
        if (activeRadio && activeRadio.getAttribute('data-virtual-size') === 'true') {
          setTimeout(enableAddToCartButtons, 10);
          setTimeout(enableAddToCartButtons, 100);
          setTimeout(enableAddToCartButtons, 350);
          setTimeout(enableAddToCartButtons, 600);
          updatePdpPrice(activeRadio);
        }
      });
    }

    // Check pre-selected size on initial page load
    const checkedRadio = getActiveSizeRadio();
    if (checkedRadio) {
      handleSizeChange(checkedRadio);
    }
  }

  // Intercept form submit in CAPTURE phase so it executes before any bubbling handlers
  document.addEventListener(
    'submit',
    function (e) {
      const form = e.target;
      if (!form || !form.matches('product-form form, [js-template-one-product-form] form, form[action*="/cart/add"]')) {
        return;
      }
      const activeRadio = getActiveSizeRadio();
      if (activeRadio && activeRadio.getAttribute('data-virtual-size') === 'true') {
        handleSizeChange(activeRadio);
      }
    },
    true
  );

  // Hook window.fetch so the AJAX body sent to /cart/add is guaranteed to contain the virtual size properties
  if (typeof window.fetch === 'function') {
    const nativeFetch = window.fetch;
    window.fetch = function (...args) {
      const url = args[0] ? String(args[0]) : '';
      if (url.includes('/cart/add')) {
        if (isVirtualSizeDisabledForPage()) {
          return nativeFetch.apply(this, args);
        }
        const activeRadio = getActiveSizeRadio();
        const isVirtual = activeRadio && activeRadio.getAttribute('data-virtual-size') === 'true';
        const options = args[1] || {};

        // Root `id` together with `items[0][id]` adds the saree twice. Root
        // `properties` become a second line that the cart then deletes.
        if (options.body && options.body instanceof FormData && options.body.has('items[0][id]')) {
          options.body.delete('id');
          ['properties[_ref_id]', 'properties[_with_upsell]'].forEach((key) => {
            if (!options.body.has(key)) return;
            const itemKey = key.replace('properties[', 'items[0][properties][');
            if (!options.body.has(itemKey)) options.body.set(itemKey, options.body.get(key));
            options.body.delete(key);
          });
        }

        const ownVariantId = activeRadio ? activeRadio.getAttribute('varid') : '';
        const sourceVariantIdForGuard = activeRadio
          ? activeRadio.getAttribute('data-source-variant-id') || ownVariantId
          : '';
        // Pre Drape / Fall & Pico are added first, as items[0]. Rewriting that
        // line swaps Pre Drape for a second saree and leaves Fall & Pico behind.
        const isUpsellAdd = isUpsellCartAdd(options.body, ownVariantId, sourceVariantIdForGuard);

        const perItemVirtualRewrite = options.body instanceof FormData
          ? rewriteFormDataItemsForVirtualSizes(options.body)
          : (typeof options.body === 'string' ? (function () {
              try {
                const parsedBody = JSON.parse(options.body);
                const changed = rewriteJsonItemsForVirtualSizes(parsedBody);
                if (changed) options.body = JSON.stringify(parsedBody);
                return changed;
              } catch (e) {
                return false;
              }
            })() : false);

        if (isVirtual && !isUpsellAdd && !perItemVirtualRewrite) {
          const requestedSize = activeRadio.getAttribute('data-requested-size') || activeRadio.value;
          const sourceSize = activeRadio.getAttribute('data-source-size') || '';
          const sourceVariantId = sourceVariantIdForGuard;
          const sourceSku = activeRadio.getAttribute('data-source-sku') || '';
          const alterationInstruction =
            activeRadio.getAttribute('data-alteration-instruction') || `Alter ${sourceSize} -> ${requestedSize}`;

          if (options.body && options.body instanceof FormData) {
            // FIX v1.1: Use sourceVariantId so Shopify receives the in-stock S variant,
            // not the out-of-stock XS variant. ownVariantId is preserved only in properties.
            if (sourceVariantId) {
              if (options.body.has('items[0][id]')) {
                options.body.set('items[0][id]', sourceVariantId); // ✅ FIXED: was ownVariantId
              }
              if (options.body.has('id')) {
                options.body.set('id', sourceVariantId);           // ✅ FIXED: was ownVariantId
              }
            }
            // Populate line item properties:
            // Visible to customer on Shopify Checkout screen
            options.body.set('items[0][properties][Requested Size]', requestedSize);
            // Internal properties for Vinculum, Cart display & warehouse
            options.body.set('items[0][properties][_requested_size]', requestedSize);
            options.body.set('items[0][properties][_requested_variant_id]', ownVariantId || '');
            options.body.set('items[0][properties][_requested_price]', activeRadio.getAttribute('data-product-price') || '');
            options.body.set('items[0][properties][_source_size]', sourceSize);
            options.body.set('items[0][properties][_source_variant_id]', sourceVariantId);
            options.body.set('items[0][properties][_source_sku]', sourceSku);
            options.body.set('items[0][properties][_alteration_required]', 'true');
            options.body.set('items[0][properties][_alteration_instruction]', alterationInstruction);
            options.body.set('items[0][properties][_non_returnable_due_to_alteration]', 'true');
            options.body.set('items[0][properties][_Delivery type]', 'RTS');
          } else if (options.body && typeof options.body === 'string') {
            try {
              const parsedBody = JSON.parse(options.body);
              if (parsedBody && Array.isArray(parsedBody.items) && parsedBody.items.length > 0) {
                const first = parsedBody.items[0];
                // FIX v1.1: Use sourceVariantId for the actual cart item ID submitted to Shopify
                first.id = Number(sourceVariantId) || sourceVariantId; // ✅ FIXED: was ownVariantId
                first.properties = first.properties || {};
                first.properties['Requested Size'] = requestedSize;
                first.properties['_requested_size'] = requestedSize;
                first.properties['_requested_variant_id'] = ownVariantId || '';
                first.properties['_requested_price'] = activeRadio.getAttribute('data-product-price') || '';
                first.properties['_source_size'] = sourceSize;
                first.properties['_source_variant_id'] = sourceVariantId;
                first.properties['_source_sku'] = sourceSku;
                first.properties['_alteration_required'] = 'true';
                first.properties['_alteration_instruction'] = alterationInstruction;
                first.properties['_non_returnable_due_to_alteration'] = 'true';
                first.properties['_Delivery type'] = 'RTS';
                options.body = JSON.stringify(parsedBody);
              }
            } catch (e) {}
          }
        } else if (!isUpsellAdd) {
          // Standard in-stock size: Strip out any stale virtual size properties from the FormData
          if (options.body && options.body instanceof FormData) {
            options.body.delete('items[0][properties][Requested Size]');
            options.body.delete('items[0][properties][_requested_size]');
            options.body.delete('items[0][properties][_requested_variant_id]');
            options.body.delete('items[0][properties][_requested_price]');
            options.body.delete('items[0][properties][_source_size]');
            options.body.delete('items[0][properties][_source_variant_id]');
            options.body.delete('items[0][properties][_source_sku]');
            options.body.delete('items[0][properties][_alteration_required]');
            options.body.delete('items[0][properties][_alteration_instruction]');
            options.body.delete('items[0][properties][_non_returnable_due_to_alteration]');
            options.body.delete('properties[Requested Size]');
            options.body.delete('properties[_requested_size]');
            options.body.delete('properties[_requested_variant_id]');
            options.body.delete('properties[_requested_price]');
            options.body.delete('properties[_source_size]');
            options.body.delete('properties[_source_variant_id]');
            options.body.delete('properties[_source_sku]');
            options.body.delete('properties[_alteration_required]');
            options.body.delete('properties[_alteration_instruction]');
            options.body.delete('properties[_non_returnable_due_to_alteration]');
          }
          try {
            sessionStorage.removeItem('kalki_vsi_cart_items');
          } catch (e) {}
        }
      }
      return nativeFetch.apply(this, args).then((res) => {
        if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update')) {
          setTimeout(syncCartRequestedSizes, 80);
          setTimeout(syncCartRequestedSizes, 250);
          setTimeout(syncCartRequestedSizes, 600);
          setTimeout(syncCartRequestedSizes, 1200);
        }
        if (url.includes('/cart.js') || (url.includes('/cart') && !url.includes('.liquid') && !url.includes('.css') && !url.includes('.js'))) {
          try {
            const origJson = res.json;
            res.json = function () {
              return origJson.apply(this).then((data) => {
                return window.kalkiTransformCartData(data);
              });
            };
          } catch (e) {}
        }
        return res;
      });
    };
  }

  // Global Cart Transformer function
  window.kalkiTransformCartData = function (data) {
    if (!data || !Array.isArray(data.items)) return data;
    data.items.forEach((item) => {
      const props = item.properties || {};
      const isVirtual = props._requested_variant_id || props.requested_variant_id || props._requested_size || props['Requested Size'] || props._virtual_inventory;
      if (isVirtual) {
        const reqId = props._requested_variant_id || props.requested_variant_id;
        if (reqId) {
          item.variant_id = Number(reqId) || reqId;
          item.id = Number(reqId) || reqId;
        }
      }
    });
    return data;
  };

  // Intercept XMLHttpRequest for discount apps (Anglerfox / CSApps / Revy / jQuery)
  if (typeof XMLHttpRequest !== 'undefined' && XMLHttpRequest.prototype) {
    const origXhrOpen = XMLHttpRequest.prototype.open;
    const origXhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._kalkiReqUrl = url ? String(url) : '';
      return origXhrOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this._kalkiReqUrl && (this._kalkiReqUrl.includes('/cart.js') || (this._kalkiReqUrl.includes('/cart') && !this._kalkiReqUrl.includes('.liquid') && !this._kalkiReqUrl.includes('.css') && !this._kalkiReqUrl.includes('.js')))) {
        this.addEventListener('readystatechange', function () {
          if (this.readyState === 4 && this.status === 200) {
            try {
              const raw = this.responseText;
              if (raw && (raw.includes('"items"') || raw.includes('"token"'))) {
                const parsed = JSON.parse(raw);
                window.kalkiTransformCartData(parsed);
                const str = JSON.stringify(parsed);
                Object.defineProperty(this, 'responseText', { value: str, writable: true, configurable: true });
                Object.defineProperty(this, 'response', { value: str, writable: true, configurable: true });
              }
            } catch (e) {}
          }
        }, true);
      }
      return origXhrSend.apply(this, arguments);
    };
  }

  // Intercept Response.prototype.json for cloned responses
  if (typeof Response !== 'undefined' && Response.prototype && Response.prototype.json) {
    const origRespJson = Response.prototype.json;
    Response.prototype.json = function () {
      const url = this.url ? String(this.url) : '';
      return origRespJson.apply(this, arguments).then((data) => {
        if (url.includes('/cart.js') || (url.includes('/cart') && !url.includes('.liquid') && !url.includes('.css') && !url.includes('.js')) || (data && Array.isArray(data.items))) {
          window.kalkiTransformCartData(data);
        }
        return data;
      });
    };
  }

  // Double-layer guarantee: Dynamically override size display on virtual size cart items
  function syncCartRequestedSizes() {
    const items = document.querySelectorAll(
      '.cart-item, .cart-item-details-wrap, tr.cart-item, [js-cart-item-details], .cart-notification-product'
    );

    items.forEach((item) => {
      let reqSize = item.getAttribute('data-requested-size');

      if (!reqSize) {
        const propEl = item.querySelector(
          '[data-property-first="_requested_size"], [data-property-first="requested_size"], [js-item-requested-size]'
        );
        if (propEl) {
          reqSize = propEl.getAttribute('data-property-last') || propEl.value || propEl.textContent;
        }
      }

      if (reqSize && reqSize.trim() !== '') {
        reqSize = reqSize.trim();
        const options = item.querySelectorAll('.product-option, [data-cart-option-size]');
        let found = false;
        options.forEach((opt) => {
          const dt = opt.querySelector('dt');
          if (dt && dt.textContent.trim().toLowerCase().includes('size')) {
            found = true;
            const dd = opt.querySelector('dd');
            if (dd && dd.textContent.trim() !== reqSize) {
              dd.textContent = reqSize;
            }
          }
        });
        if (!found) {
          const optWrap = item.querySelector('.option-quantity-wrap');
          if (optWrap && !optWrap.querySelector('[data-cart-option-size]')) {
            const sizeDiv = document.createElement('div');
            sizeDiv.className = 'product-option';
            sizeDiv.setAttribute('data-cart-option-size', '');
            sizeDiv.innerHTML = `<dt>Size:</dt> <dd>${reqSize}</dd>`;
            optWrap.insertBefore(sizeDiv, optWrap.firstChild);
          }
        }
      }
    });
  }

  // Observe cart DOM changes (cart drawer open, quantity change, AJAX re-render)
  document.addEventListener('cart:updated', syncCartRequestedSizes);
  document.addEventListener('cart:refresh', syncCartRequestedSizes);
  document.addEventListener('click', function (e) {
    if (e.target.closest('[href*="cart"], .cart-icon, #cart-icon-bubble, button[name="add"], .product-form__submit')) {
      setTimeout(syncCartRequestedSizes, 100);
      setTimeout(syncCartRequestedSizes, 400);
      setTimeout(syncCartRequestedSizes, 900);
      setTimeout(syncCartRequestedSizes, 1800);
    }
  });

  const cartObserver = new MutationObserver(function () {
    syncCartRequestedSizes();
  });
  const drawerEl = document.querySelector('cart-drawer, #CartDrawer, #main-cart-items');
  if (drawerEl) {
    cartObserver.observe(drawerEl, { childList: true, subtree: true });
  }

  // Observe delivery message container to ensure it stays visible and clean for virtual sizes
  const mtoTarget = document.querySelector('.additional_delivery_massage_text');
  if (mtoTarget) {
    const mtoObserver = new MutationObserver(function () {
      const activeRadio = getActiveSizeRadio();
      if (activeRadio && activeRadio.getAttribute('data-virtual-size') === 'true') {
        const expressEl = document.querySelector('[js-product-express-delivery-time]');
        const expressDate = expressEl && expressEl.textContent.trim();
        if (expressDate && !mtoTarget.innerHTML.includes(expressDate)) {
          updateVirtualDeliveryMessage();
        }
      }
    });
    mtoObserver.observe(mtoTarget, { childList: true, characterData: true, subtree: true });
  }

  // Guarantee Priority express shipping property right before add to cart submit
  document.addEventListener('submit', function (e) {
    const form = e.target && e.target.closest ? e.target.closest('form[data-type="add-to-cart-form"], form[action*="/cart/add"]') : null;
    if (form) {
      const activeRadio = getActiveSizeRadio();
      if (activeRadio && activeRadio.getAttribute('data-virtual-size') === 'true') {
        updateVirtualDeliveryMessage();
      }
    }
  }, true);

  // Re-sync price and re-enable Add to Cart button if VariantSelects re-renders after network fetch
  document.addEventListener('variant:change', function () {
    const activeRadio = getActiveSizeRadio();
    if (activeRadio) {
      updatePdpPrice(activeRadio);
      if (activeRadio.getAttribute('data-virtual-size') === 'true') {
        handleSizeChange(activeRadio);
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initVirtualSizeInventory();
      syncCartRequestedSizes();
    });
  } else {
    initVirtualSizeInventory();
    syncCartRequestedSizes();
  }

  // Support Shopify section rendering / dynamic reload events
  document.addEventListener('shopify:section:load', function () {
    initVirtualSizeInventory();
    syncCartRequestedSizes();
  });
})();